// components/portal/UserSelector.tsx
import React, { useCallback, useMemo, useState } from 'react';
import { ChevronDown, UserPlus } from '@/components/ui/lucide-memo';
import clsx from 'clsx';
import { MemberData } from '#types/types';
import { useKernelStore, useKernelStoreApi } from '@/kernel/kernelStores';
import { GenericEntitySelector } from './GenericEntitySelector';

const MemoUserPlus = React.memo(UserPlus);

const translations = {
  en: { assignMember: 'Assign member', selectMember: 'Select member', everyone: 'Everyone' },
  fr: { assignMember: 'Assigner un membre', selectMember: 'Selectionner un membre', everyone: 'Tout le monde' },
};

interface UserSelectorProps {
  selectedMember: MemberData | null;
  onSelect?: (member: MemberData | null) => void;
  onClick?: () => void;
  onContextMenu?: (e: React.MouseEvent) => void;
  organisationId?: string;
  allowedUserIds?: string[];
  disabled?: boolean;
  showName?: boolean;
  /** ChatGPT's labelled picker (avatar, name, chevron; 'Everyone' when nobody is picked). */
  pill?: boolean;
}

const getMemberDisplayName = (member: MemberData | null): string => {
  if (!member) return '';
  const full = [member.firstName, member.lastName].filter(Boolean).join(' ').trim();
  return full || member.email;
};

const getMemberInitials = (member: MemberData | null): string => {
  if (!member) return '';
  const first = member.firstName?.trim()?.[0] || '';
  const last = member.lastName?.trim()?.[0] || '';
  const combined = `${first}${last}`.trim();
  if (combined) return combined.toUpperCase();
  const emailInitial = member.email?.trim()?.[0] || 'U';
  return emailInitial.toUpperCase();
};

const Trigger = React.memo(function Trigger({
  selectedMember,
  disabled,
  tooltipLabel,
  onActivate,
  onContextMenu,
  showName = false,
  pill = false,
  everyoneLabel = '',
}: {
  pill?: boolean;
  everyoneLabel?: string;
  selectedMember: MemberData | null;
  disabled?: boolean;
  tooltipLabel: string;
  onActivate?: () => void;
  onContextMenu: (e: React.MouseEvent) => void;
  showName?: boolean;
}) {
  const [failedSrc, setFailedSrc] = useState<string | null>(null);

  const avatarSrc = useMemo(() => {
    if (!selectedMember) return null;
    const rawImageUrl = selectedMember.imageUrl?.trim() || '';
    // Some payloads provide a storage path (e.g. "userId/avatar.webp"), not a directly fetchable URL.
    // Use it only when it's already a full/absolute URL; otherwise proxy via avatar API.
    if (
      rawImageUrl &&
      (rawImageUrl.startsWith('/') || /^https?:\/\//i.test(rawImageUrl) || rawImageUrl.startsWith('data:'))
    ) {
      return rawImageUrl;
    }
    return `/api/user/avatar?userId=${selectedMember.id}`;
  }, [selectedMember]);

  const memberName = getMemberDisplayName(selectedMember);
  const memberInitials = getMemberInitials(selectedMember);

  const handleClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (disabled) return;
    onActivate?.();
  }, [disabled, onActivate]);

  const handleImageError = useCallback(() => {
    setFailedSrc(avatarSrc);
  }, [avatarSrc]);

  if (pill) {
    return (
      <button
        type="button"
        onClick={handleClick}
        onContextMenu={onContextMenu}
        disabled={disabled}
        className="inline-flex h-8 max-w-[220px] shrink-0 items-center gap-2 rounded-full px-3 text-[#E5E5E5] transition-colors hover:bg-white/[0.08] hover:text-white disabled:opacity-50"
        style={{ fontSize: 14 }}
      >
        {selectedMember ? (
          avatarSrc && failedSrc !== avatarSrc
            ? <img src={avatarSrc} alt="" className="-ml-1 h-6 w-6 shrink-0 rounded-full object-cover" onError={handleImageError} />
            : <span className="-ml-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#2A2A2A] text-white" style={{ fontSize: 11 }}>{memberInitials}</span>
        ) : null}
        <span className="truncate" style={{ fontSize: 'inherit' }}>{selectedMember ? memberName : everyoneLabel}</span>
        <ChevronDown size={16} className="shrink-0 text-[#AFAFAF]" />
      </button>
    );
  }

  return (
    <div
      onClick={handleClick}
      onContextMenu={onContextMenu}
      title={tooltipLabel}
      className={clsx(
        'group/user flex h-9 shrink-0 cursor-pointer items-center rounded-full transition-colors hover:bg-white/[0.08]',
        showName ? 'max-w-[220px] gap-2.5 pl-1 pr-3' : 'w-9 justify-center',
        disabled && 'cursor-not-allowed',
      )}
    >
      <div
        className={clsx(
          'flex h-7 w-7 shrink-0 items-center justify-center overflow-hidden rounded-full',
          !selectedMember && 'text-[#E5E5E5]',
        )}
      >
        {selectedMember ? (
          avatarSrc && failedSrc !== avatarSrc ? (
            <img
              src={avatarSrc}
              alt={memberName}
              className="h-full w-full object-cover"
              loading="lazy"
              onError={handleImageError}
            />
          ) : (
            <span className="text-[12px] font-semibold leading-none text-white">
              {memberInitials}
            </span>
          )
        ) : (
          <MemoUserPlus size={18} className="text-[#E5E5E5] transition-colors group-hover/user:text-white" />
        )}
      </div>
      {showName && selectedMember && (
        <span className="truncate text-[14px] leading-[14px] text-white">
          {memberName}
        </span>
      )}
    </div>
  );
});

export const UserSelector = React.memo(({
  selectedMember,
  onSelect,
  onClick,
  onContextMenu,
  organisationId,
  allowedUserIds,
  disabled,
  showName = false,
  pill = false,
}: UserSelectorProps) => {
  const lang = useKernelStore((state) => state.language);
  const kernelStore = useKernelStoreApi();
  const t = translations[lang];
  const selectorFilters = useMemo(
    () => ({
      organisationId: organisationId || undefined,
      ids: Array.isArray(allowedUserIds) && allowedUserIds.length > 0 ? [...allowedUserIds].sort() : undefined
    }),
    [organisationId, allowedUserIds]
  );

  const isSelectorMode = Boolean(onSelect) && !onClick;

  const tooltipLabel = selectedMember
    ? ([selectedMember.firstName, selectedMember.lastName].filter(Boolean).join(' ') || selectedMember.email)
    : t.assignMember;

  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    if (onSelect && selectedMember) {
      e.preventDefault();
      e.stopPropagation();
      onSelect(null);
      return;
    }
    if (disabled) return;
    if (onContextMenu) {
      e.stopPropagation();
      onContextMenu(e);
    }
  }, [disabled, onContextMenu, onSelect, selectedMember]);

  const selectedIds = useMemo(
    () => (selectedMember ? [selectedMember.id] : []),
    [selectedMember],
  );

  const handleSelect = useCallback((id: string) => {
    const member = kernelStore.getState().inventory.User.registry[id];
    if (member && onSelect) onSelect(member as MemberData);
  }, [onSelect, kernelStore]);

  const renderTrigger = useCallback((_items: unknown[], openSelector: () => void) => (
    <Trigger
      selectedMember={selectedMember}
      disabled={disabled}
      tooltipLabel={tooltipLabel}
      showName={showName}
      pill={pill}
      everyoneLabel={t.everyone}
      onActivate={openSelector}
      onContextMenu={handleContextMenu}
    />
  ), [selectedMember, disabled, tooltipLabel, showName, pill, t.everyone, handleContextMenu]);

  if (!isSelectorMode) {
    return (
      <Trigger
        selectedMember={selectedMember}
        disabled={disabled}
        tooltipLabel={tooltipLabel}
        showName={showName}
        onActivate={onClick}
        onContextMenu={handleContextMenu}
      />
    );
  }

  return (
    <GenericEntitySelector
      type="User"
      selectedIds={selectedIds}
      onSelect={handleSelect}
      filters={selectorFilters}
      variant="row"
      title={t.selectMember}
      renderTrigger={renderTrigger}
    />
  );
});

UserSelector.displayName = 'UserSelector';
